import { ReactNode, useEffect, useRef, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';

const CARD_ELEMENT_OPTIONS = { hidePostalCode: true } as const;

export type StripeCardElement = {
  mount: (element: HTMLElement) => void;
  clear: () => void;
  destroy?: () => void;
  on?: (event: 'ready', handler: () => void) => void;
};

type StripeElements = {
  create: (type: string, options?: unknown) => StripeCardElement;
};

export type StripeClient = {
  elements: () => StripeElements;
  createToken: (
    card: StripeCardElement
  ) => Promise<{ token?: { id: string }; error?: { message?: string } }>;
  confirmCardPayment: (
    intent: string,
    payload: { payment_method: { card: StripeCardElement } }
  ) => Promise<{ paymentIntent?: { status?: string }; error?: { message?: string } }>;
};

declare global {
  interface Window {
    Stripe?: (key: string) => StripeClient;
  }
}

const loadStripeScript = () => {
  return new Promise<void>((resolve, reject) => {
    if (window.Stripe) {
      resolve();
      return;
    }

    const existing = document.querySelector(
      'script[data-stripe-v3="true"]'
    ) as HTMLScriptElement | null;

    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Unable to load Stripe')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.dataset.stripeV3 = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unable to load Stripe'));

    document.head.appendChild(script);
  });
};

const resolveStripeKey = async () => {
  const envKey = import.meta.env.VITE_STRIPE_CLIENT_KEY;
  if (typeof envKey === 'string' && envKey.trim()) {
    return envKey;
  }

  try {
    const response = await fetch('/config');
    if (response.ok) {
      const config = (await response.json()) as { stripeKey?: string };
      if (typeof config.stripeKey === 'string' && config.stripeKey.trim()) {
        return config.stripeKey;
      }
    }
  } catch {
    // Fall through to no key.
  }

  return '';
};

type StripeElementType = 'card';

type MountedStripeFieldProps = {
  elements: StripeElements;
  type: StripeElementType;
  options?: unknown;
  onReady?: (element: StripeCardElement) => void;
};

const MountedStripeField = ({ elements, type, options, onReady }: MountedStripeFieldProps) => {
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const onReadyRef = useRef(onReady);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    if (!fieldRef.current) {
      return;
    }

    const stripeElement = elements.create(type, options);
    stripeElement.mount(fieldRef.current);

    const notifyReady = () => {
      onReadyRef.current?.(stripeElement);
    };

    if (stripeElement.on) {
      stripeElement.on('ready', notifyReady);
    } else {
      notifyReady();
    }

    return () => {
      stripeElement.destroy?.();
    };
  }, [elements, options, type]);

  const paymentFieldSx = {
    border: '1px solid #b9b9b9',
    borderRadius: '2px',
    px: 1.25,
    py: 1.1,
    minHeight: 44,
    backgroundColor: '#ffffff',
  } as const;

  return <Box ref={fieldRef} sx={paymentFieldSx} />;
};

type StripePaymentFieldsProps = {
  visible: boolean;
  title?: ReactNode;
  helperText?: ReactNode;
  onReadyChange: (client: StripeClient | null, card: StripeCardElement | null) => void;
};

export const StripePaymentFields = ({
  visible,
  title,
  helperText,
  onReadyChange,
}: StripePaymentFieldsProps) => {
  const [stripeError, setStripeError] = useState('');
  const [stripeClient, setStripeClient] = useState<StripeClient | null>(null);
  const [stripeElements, setStripeElements] = useState<StripeElements | null>(null);
  const stripeCardElementRef = useRef<StripeCardElement | null>(null);

  useEffect(() => {
    if (!visible) {
      setStripeClient(null);
      setStripeElements(null);
      stripeCardElementRef.current = null;
      onReadyChange(null, null);
      return;
    }

    let mounted = true;

    const setupStripe = async () => {
      try {
        setStripeError('');
        await loadStripeScript();
        const key = await resolveStripeKey();

        if (!key || !window.Stripe) {
          if (mounted) {
            setStripeError('Stripe is not configured for this app environment.');
            onReadyChange(null, null);
          }
          return;
        }

        const client = window.Stripe(key);
        if (!mounted) {
          return;
        }

        setStripeClient(client);
        setStripeElements(client.elements());
        onReadyChange(client, stripeCardElementRef.current);
      } catch {
        if (mounted) {
          setStripeClient(null);
          setStripeElements(null);
          setStripeError('Unable to load Stripe payment details.');
          onReadyChange(null, null);
        }
      }
    };

    void setupStripe();

    return () => {
      mounted = false;
      stripeCardElementRef.current = null;
      onReadyChange(null, null);
    };
  }, [onReadyChange, visible]);

  useEffect(() => {
    if (!visible) {
      setStripeClient(null);
      stripeCardElementRef.current = null;
    }
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <Box
      sx={{
        border: '1px solid #e7e7e7',
        borderCollapse: 'collapse',
        px: 2,
        py: 1.5,
      }}
    >
      {title ? <Typography sx={{ fontWeight: 700, color: '#022b3a' }}>{title}</Typography> : null}
      {stripeError ? <Alert severity="warning">{stripeError}</Alert> : null}
      <Stack spacing={1.5}>
        <Typography sx={{ fontWeight: 600, color: '#022b3a', whiteSpace: 'nowrap' }}>
          Payment details
        </Typography>
        {stripeElements ? (
          <MountedStripeField
            elements={stripeElements}
            type="card"
            options={CARD_ELEMENT_OPTIONS}
            onReady={(element) => {
              stripeCardElementRef.current = element;
              onReadyChange(stripeClient, element);
            }}
          />
        ) : null}
      </Stack>
      <Typography sx={{ mt: 1, fontSize: 11, fontWeight: 700, color: '#625dcf' }}>
        {helperText ?? '(Powered by Stripe)'}
      </Typography>
    </Box>
  );
};
